import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  View,
  Text,
  Pressable,
  ScrollView,
  Image,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  ChevronLeft,
  Shield,
  ShieldCheck,
  ShieldOff,
  Smartphone,
  Key,
  AlertTriangle,
  CheckCircle,
  Copy,
  RefreshCw,
  Sparkles,
  ChevronRight,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { track, useTrackScreen } from '@/lib/analytics';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';
import { useAuth, isSupabaseConfigured } from '@/lib/supabase';
import { useAlert } from '@/components/AlertDialog';
import * as Clipboard from 'expo-clipboard';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { getUserMessage } from '@/lib/errors';
import AnimatedPressable from '@/components/AnimatedPressable';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';
import { AiConsentManagerModal } from '@/components/consent/AiConsentManagerModal';
import { APP_LOCALE } from '@/lib/format';

type MFAState = 'loading' | 'disabled' | 'setup' | 'verify' | 'enabled';

interface MFAFactor {
  id: string;
  status: string;
  friendly_name: string;
  created_at: string;
}

export default function SecurityScreen() {
  useTrackScreen('Security');
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const { alert, error: showError, confirm } = useAlert();
  const {
    user,
    isConfigured,
    enrollMFA,
    verifyMFAEnrollment,
    verifyActiveMFA,
    unenrollMFA,
    getMFAFactors,
  } = useAuth();
  const userRole = useSubscriptionStore((s) => s.userRole);

  const [mfaState, setMfaState] = useState<MFAState>('loading');
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [activeFactor, setActiveFactor] = useState<MFAFactor | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showDisableVerify, setShowDisableVerify] = useState(false);
  const [disableVerifyCode, setDisableVerifyCode] = useState('');
  const [showAiConsentManager, setShowAiConsentManager] = useState(false);

  // Dynamic breadcrumbs based on user role
  const profileHref = userRole === 'startup'
    ? '/(startup)/userprofile'
    : userRole === 'admin'
    ? '/(admin)/profile'
    : '/(investor)/investorprofile';

  const breadcrumbs: Breadcrumb[] = [
    { label: 'Profile', href: profileHref },
    { label: 'Settings', href: '/settings' },
    { label: 'Security' },
  ];

  // Load MFA status on mount
  useEffect(() => {
    loadMFAStatus();
  }, []);

  const loadMFAStatus = async () => {
    if (!isConfigured || !isSupabaseConfigured()) {
      setMfaState('disabled');
      setActiveFactor(null);
      return;
    }

    const { factors, error } = await getMFAFactors();

    if (error) {
      setMfaState('disabled');
      setActiveFactor(null);
      return;
    }

    const verifiedFactor = factors.find((f: MFAFactor) => f.status === 'verified');

    if (verifiedFactor) {
      setActiveFactor(verifiedFactor);
      setMfaState('enabled');
    } else {
      setActiveFactor(null);
      setMfaState('disabled');
    }
  };

  const enableMutation = useMutation({
    mutationFn: async () => {
      const result = await enrollMFA();

      if (result.error) {
        showError('Setup Failed', getUserMessage(result.error));
        return;
      }

      setQrCode(result.qrCode || null);
      setSecret(result.secret || null);
      setFactorId(result.factorId || null);
      setMfaState('setup');
    },
  });

  const handleEnableMFA = () => {
    if (!isConfigured || !isSupabaseConfigured()) {
      setQrCode(null);
      setSecret('DEMO-ABCD-EFGH-1234-5678');
      setFactorId('demo-factor-id');
      setMfaState('setup');
      return;
    }

    enableMutation.mutate();
  };

  const verifySetupMutation = useMutation({
    mutationFn: async () => {
      if (!isConfigured || !isSupabaseConfigured()) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        setMfaState('enabled');
        setActiveFactor({
          id: 'demo-factor-id',
          status: 'verified',
          friendly_name: 'Authenticator App',
          created_at: new Date().toISOString(),
        });
        track('mfa_enabled');
        alert('2FA Enabled', 'Two-factor authentication has been successfully enabled for your account.');
        return;
      }

      try {
        const result = await verifyMFAEnrollment(factorId!, verifyCode);

        if (result.error || !result.success) {
          showError('Verification Failed', getUserMessage(result.error));
          setVerifyCode('');
          return;
        }

        const verifiedFactorId = factorId;

        setQrCode(null);
        setSecret(null);
        setVerifyCode('');
        setFactorId(null);

        setActiveFactor({
          id: verifiedFactorId!,
          status: 'verified',
          friendly_name: 'Authenticator App',
          created_at: new Date().toISOString(),
        });
        setMfaState('enabled');

        track('mfa_enabled');
        alert('2FA Enabled', 'Two-factor authentication has been successfully enabled for your account.');
      } catch (err) {
        showError('Verification Failed', 'An unexpected error occurred. Please try again.');
      }
    },
  });

  const handleVerifySetup = () => {
    if (verifyCode.length !== 6) {
      alert('Invalid Code', 'Please enter the 6-digit code from your authenticator app.');
      return;
    }

    if (!factorId) {
      showError('Error', 'Missing factor ID. Please try setup again.');
      return;
    }

    verifySetupMutation.mutate();
  };

  const handleDisableMFA = async () => {
    const confirmed = await confirm(
      'Disable 2FA',
      'Are you sure you want to disable two-factor authentication? You\'ll need to enter a code from your authenticator app to confirm.'
    );

    if (!confirmed) return;

    if (!activeFactor?.id) {
      showError('Error', 'No active factor to disable.');
      return;
    }

    if (!isConfigured || !isSupabaseConfigured()) {
      // Demo mode
      setMfaState('disabled');
      setActiveFactor(null);
      track('mfa_disabled');
      alert('2FA Disabled', 'Two-factor authentication has been disabled.');
      return;
    }

    // Show verification input for disabling
    setShowDisableVerify(true);
    setDisableVerifyCode('');
  };

  const confirmDisableMutation = useMutation({
    mutationFn: async () => {
      try {
        const { error, success } = await unenrollMFA(activeFactor!.id, disableVerifyCode);

        if (error || !success) {
          showError('Error', error ? getUserMessage(error) : 'Failed to disable 2FA. Please check your code and try again.');
          setDisableVerifyCode('');
          return;
        }

        setShowDisableVerify(false);
        setDisableVerifyCode('');

        setActiveFactor(null);
        setMfaState('disabled');

        track('mfa_disabled');
        alert('2FA Disabled', 'Two-factor authentication has been disabled.');
      } catch (err) {
        showError('Error', 'An unexpected error occurred. Please try again.');
      }
    },
  });

  const isLoading = enableMutation.isPending || verifySetupMutation.isPending || confirmDisableMutation.isPending;

  const handleConfirmDisable = () => {
    if (disableVerifyCode.length !== 6) {
      alert('Invalid Code', 'Please enter the 6-digit code from your authenticator app.');
      return;
    }

    if (!activeFactor?.id) {
      showError('Error', 'No active factor to disable.');
      return;
    }

    confirmDisableMutation.mutate();
  };

  const handleCancelDisable = () => {
    setShowDisableVerify(false);
    setDisableVerifyCode('');
  };

  const handleCopySecret = async () => {
    if (secret) {
      await Clipboard.setStringAsync(secret);
      alert('Copied', 'Secret key copied to clipboard. You can manually enter it in your authenticator app.');
    }
  };

  const handleCancelSetup = () => {
    setMfaState('disabled');
    setQrCode(null);
    setSecret(null);
    setFactorId(null);
    setVerifyCode('');
  };

  return (
    <AppLayout title="Security" breadcrumbs={breadcrumbs}>
      <View key={`security-${refreshKey}`} className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
          <KeyboardAwareBody className="flex-1" showsVerticalScrollIndicator={false}>
            {/* Header - Mobile only */}
            {phoneShell && (
              <Animated.View
                entering={FadeInDown.delay(100).duration(500)}
                className="flex-row items-center gap-4 px-5 pt-4 pb-6"
              >
                <AnimatedPressable
                  onPress={() => router.back()}
                  className="w-10 h-10 rounded-full items-center justify-center"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  <ChevronLeft size={24} color={colors.text.primary} />
                </AnimatedPressable>
                <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
                  Security
                </Text>
              </Animated.View>
            )}

            <View className={`px-5 pb-8 ${isWeb ? 'pt-6' : ''}`}>
              {/* Security Overview Card */}
              <Animated.View entering={FadeInDown.delay(200).duration(500)} className="mb-6">
                <View
                  className="rounded-2xl p-5"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.border.light,
                  }}
                >
                  <View className="flex-row items-center gap-4 mb-4">
                    <View
                      className="w-14 h-14 rounded-full items-center justify-center"
                      style={{
                        backgroundColor: mfaState === 'enabled' ? colors.success[50] : colors.warning[50],
                      }}
                    >
                      {mfaState === 'enabled' ? (
                        <ShieldCheck size={28} color={colors.success[600]} />
                      ) : (
                        <Shield size={28} color={colors.warning[600]} />
                      )}
                    </View>
                    <View className="flex-1">
                      <Text style={{ color: colors.text.primary }} className="text-lg font-bold">
                        Account Security
                      </Text>
                      <Text style={{ color: colors.text.secondary }} className="text-sm">
                        {mfaState === 'enabled'
                          ? 'Your account is protected with 2FA'
                          : 'Enable 2FA for enhanced security'}
                      </Text>
                    </View>
                  </View>

                  {/* Security Status */}
                  <View className="flex-row items-center gap-2 py-2 px-3 rounded-xl" style={{ backgroundColor: colors.surface.secondary }}>
                    <View
                      className="w-2 h-2 rounded-full"
                      style={{
                        backgroundColor: mfaState === 'enabled' ? colors.success[500] : colors.warning[500],
                      }}
                    />
                    <Text style={{ color: colors.text.secondary }} className="text-sm">
                      {mfaState === 'enabled' ? 'Two-Factor Authentication: Active' : 'Two-Factor Authentication: Inactive'}
                    </Text>
                  </View>
                </View>
              </Animated.View>

              {/* Loading State */}
              {mfaState === 'loading' && (
                <Animated.View entering={FadeInUp.duration(400)} className="items-center py-12">
                  <ActivityIndicator size="large" color={colors.primary[600]} />
                  <Text style={{ color: colors.text.secondary }} className="mt-4 text-base">
                    Loading security settings...
                  </Text>
                </Animated.View>
              )}

              {/* MFA Disabled State */}
              {mfaState === 'disabled' && (
                <Animated.View entering={FadeInUp.delay(300).duration(500)}>
                  <View
                    className="rounded-2xl overflow-hidden"
                    style={{
                      backgroundColor: colors.surface.primary,
                      borderWidth: 1,
                      borderColor: colors.border.light,
                    }}
                  >
                    <View className="p-5 border-b" style={{ borderColor: colors.border.light }}>
                      <View className="flex-row items-center gap-3 mb-3">
                        <Smartphone size={24} color={colors.primary[600]} />
                        <Text style={{ color: colors.text.primary }} className="text-lg font-semibold">
                          Two-Factor Authentication
                        </Text>
                      </View>
                      <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
                        Add an extra layer of security to your account. When enabled, you'll need to enter a code from your authenticator app in addition to your password.
                      </Text>
                    </View>

                    <View className="p-5">
                      <AnimatedPressable
                        onPress={handleEnableMFA}
                        disabled={isLoading}
                        className="rounded-xl py-4 items-center"
                        style={{ backgroundColor: colors.primary[600] }}
                      >
                        <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                          {isLoading ? 'Setting up...' : 'Enable 2FA'}
                        </Text>
                      </AnimatedPressable>
                    </View>
                  </View>

                  {/* Warning */}
                  <View
                    className="flex-row items-start gap-3 mt-4 p-4 rounded-xl"
                    style={{ backgroundColor: colors.warning[50], borderWidth: 1, borderColor: colors.warning[200] }}
                  >
                    <AlertTriangle size={20} color={colors.warning[600]} />
                    <Text style={{ color: colors.warning[700] }} className="flex-1 text-sm leading-relaxed">
                      We strongly recommend enabling two-factor authentication to protect your account from unauthorized access.
                    </Text>
                  </View>
                </Animated.View>
              )}

              {/* MFA Setup State */}
              {mfaState === 'setup' && (
                <Animated.View entering={FadeInUp.delay(300).duration(500)}>
                  <View
                    className="rounded-2xl overflow-hidden"
                    style={{
                      backgroundColor: colors.surface.primary,
                      borderWidth: 1,
                      borderColor: colors.border.light,
                    }}
                  >
                    <View className="p-5 border-b" style={{ borderColor: colors.border.light }}>
                      <Text style={{ color: colors.text.primary }} className="text-lg font-semibold mb-2">
                        Set Up Authenticator App
                      </Text>
                      <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
                        Scan the QR code below with your authenticator app (Google Authenticator, Authy, 1Password, etc.)
                      </Text>
                    </View>

                    {/* QR Code */}
                    <View className="items-center py-6 px-5 border-b" style={{ borderColor: colors.border.light }}>
                      {qrCode ? (
                        // QR code from Supabase is a data URL (data:image/svg+xml;utf-8,...)
                        <View
                          style={{
                            width: 200,
                            height: 200,
                            borderRadius: 12,
                            overflow: 'hidden',
                            backgroundColor: '#fff',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {isWeb ? (
                            // Use native img tag on web for SVG data URL support
                            <img
                              src={qrCode}
                              alt="MFA QR Code"
                              style={{ width: 200, height: 200, objectFit: 'contain' }}
                            />
                          ) : (
                            <Image
                              source={{ uri: qrCode }}
                              style={{ width: 200, height: 200 }}
                              resizeMode="contain"
                            />
                          )}
                        </View>
                      ) : (
                        <View
                          className="w-48 h-48 rounded-xl items-center justify-center p-4"
                          style={{ backgroundColor: colors.surface.secondary, borderWidth: 2, borderColor: colors.border.light, borderStyle: 'dashed' }}
                        >
                          <Smartphone size={40} color={colors.primary[500]} />
                          <Text style={{ color: colors.text.secondary }} className="mt-3 text-sm font-medium text-center">
                            Demo Mode
                          </Text>
                          <Text style={{ color: colors.text.tertiary }} className="mt-1 text-xs text-center">
                            Use the secret key below to add manually
                          </Text>
                        </View>
                      )}
                    </View>

                    {/* Manual Entry */}
                    {secret && (
                      <View className="p-5 border-b" style={{ borderColor: colors.border.light }}>
                        <Text style={{ color: colors.text.secondary }} className="text-sm mb-2">
                          Can't scan? Enter this key manually:
                        </Text>
                        <AnimatedPressable
                          onPress={handleCopySecret}
                          className="flex-row items-center justify-between p-3 rounded-xl"
                          style={{ backgroundColor: colors.surface.secondary }}
                        >
                          <Text style={{ color: colors.text.primary }} className="font-mono text-sm flex-1 mr-2">
                            {secret}
                          </Text>
                          <Copy size={18} color={colors.primary[600]} />
                        </AnimatedPressable>
                      </View>
                    )}

                    {/* Verification */}
                    <View className="p-5">
                      <Text style={{ color: colors.text.secondary }} className="text-sm mb-3">
                        Enter the 6-digit code from your authenticator app:
                      </Text>
                      <TextInput
                        style={{
                          color: colors.text.primary,
                          backgroundColor: colors.surface.secondary,
                          borderWidth: 1,
                          borderColor: colors.border.light,
                        }}
                        className="rounded-xl px-4 py-3 text-lg text-center font-mono tracking-widest"
                        placeholder="000000"
                        placeholderTextColor={colors.text.tertiary}
                        value={verifyCode}
                        onChangeText={(text) => setVerifyCode(text.replace(/[^0-9]/g, '').slice(0, 6))}
                        keyboardType="number-pad"
                        maxLength={6}
                      />

                      <View className="flex-row gap-3 mt-4">
                        <AnimatedPressable
                          onPress={handleCancelSetup}
                          className="flex-1 rounded-xl py-3 items-center"
                          style={{ backgroundColor: colors.surface.secondary }}
                        >
                          <Text style={{ color: colors.text.secondary }} className="font-semibold">
                            Cancel
                          </Text>
                        </AnimatedPressable>
                        <AnimatedPressable
                          onPress={handleVerifySetup}
                          disabled={isLoading || verifyCode.length !== 6}
                          className="flex-1 rounded-xl py-3 items-center"
                          style={{
                            backgroundColor: verifyCode.length === 6 ? colors.primary[600] : colors.border.medium,
                          }}
                        >
                          <Text style={{ color: colors.text.inverse }} className="font-semibold">
                            {isLoading ? 'Verifying...' : 'Verify & Enable'}
                          </Text>
                        </AnimatedPressable>
                      </View>
                    </View>
                  </View>
                </Animated.View>
              )}

              {/* MFA Enabled State */}
              {mfaState === 'enabled' && (
                <Animated.View entering={FadeInUp.delay(300).duration(500)}>
                  <View
                    className="rounded-2xl overflow-hidden"
                    style={{
                      backgroundColor: colors.surface.primary,
                      borderWidth: 1,
                      borderColor: colors.border.light,
                    }}
                  >
                    <View className="p-5 border-b" style={{ borderColor: colors.border.light }}>
                      <View className="flex-row items-center gap-3 mb-3">
                        <View
                          className="w-10 h-10 rounded-full items-center justify-center"
                          style={{ backgroundColor: colors.success[50] }}
                        >
                          <CheckCircle size={24} color={colors.success[600]} />
                        </View>
                        <View className="flex-1">
                          <Text style={{ color: colors.text.primary }} className="text-lg font-semibold">
                            2FA is Active
                          </Text>
                          <Text style={{ color: colors.text.secondary }} className="text-sm">
                            Authenticator App
                          </Text>
                        </View>
                      </View>
                      <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
                        You'll be asked to enter a verification code when signing in on a new device or browser.
                      </Text>
                    </View>

                    {activeFactor && (
                      <View className="p-5 border-b" style={{ borderColor: colors.border.light }}>
                        <View className="flex-row items-center justify-between">
                          <View>
                            <Text style={{ color: colors.text.primary }} className="font-medium">
                              {activeFactor.friendly_name || 'Authenticator App'}
                            </Text>
                            <Text style={{ color: colors.text.tertiary }} className="text-sm">
                              Added {new Date(activeFactor.created_at).toLocaleDateString(APP_LOCALE)}
                            </Text>
                          </View>
                          <View className="px-3 py-1 rounded-full" style={{ backgroundColor: colors.success[50] }}>
                            <Text style={{ color: colors.success[600] }} className="text-xs font-semibold">
                              Active
                            </Text>
                          </View>
                        </View>
                      </View>
                    )}

                    <View className="p-5">
                      {showDisableVerify ? (
                        <View>
                          <Text style={{ color: colors.text.secondary }} className="text-sm mb-3">
                            Enter the 6-digit code from your authenticator app to confirm:
                          </Text>
                          <TextInput
                            style={{
                              color: colors.text.primary,
                              backgroundColor: colors.surface.secondary,
                              borderWidth: 1,
                              borderColor: colors.border.light,
                            }}
                            className="rounded-xl px-4 py-3 text-lg text-center font-mono tracking-widest mb-4"
                            placeholder="000000"
                            placeholderTextColor={colors.text.tertiary}
                            value={disableVerifyCode}
                            onChangeText={(text) => setDisableVerifyCode(text.replace(/[^0-9]/g, '').slice(0, 6))}
                            keyboardType="number-pad"
                            maxLength={6}
                          />
                          <View className="flex-row gap-3">
                            <AnimatedPressable
                              onPress={handleCancelDisable}
                              className="flex-1 rounded-xl py-3 items-center"
                              style={{ backgroundColor: colors.surface.secondary }}
                            >
                              <Text style={{ color: colors.text.secondary }} className="font-semibold">
                                Cancel
                              </Text>
                            </AnimatedPressable>
                            <AnimatedPressable
                              onPress={handleConfirmDisable}
                              disabled={isLoading || disableVerifyCode.length !== 6}
                              className="flex-1 flex-row items-center justify-center gap-2 rounded-xl py-3"
                              style={{
                                backgroundColor: disableVerifyCode.length === 6 ? colors.error : colors.border.medium,
                              }}
                            >
                              <Text style={{ color: colors.text.inverse }} className="font-semibold">
                                {isLoading ? 'Disabling...' : 'Confirm Disable'}
                              </Text>
                            </AnimatedPressable>
                          </View>
                        </View>
                      ) : (
                        <AnimatedPressable
                          onPress={handleDisableMFA}
                          disabled={isLoading}
                          className="flex-row items-center justify-center gap-2 rounded-xl py-3"
                          style={{ backgroundColor: colors.error + '15' }}
                        >
                          <ShieldOff size={20} color={colors.error} />
                          <Text style={{ color: colors.error }} className="font-semibold">
                            {isLoading ? 'Disabling...' : 'Disable 2FA'}
                          </Text>
                        </AnimatedPressable>
                      )}
                    </View>
                  </View>
                </Animated.View>
              )}

              {/* Password Section */}
              <Animated.View entering={FadeInUp.delay(400).duration(500)} className="mt-6">
                <View
                  className="rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.border.light,
                  }}
                >
                  <AnimatedPressable
                    onPress={() => router.push('/change-password')}
                    className="flex-row items-center p-5"
                  >
                    <View
                      className="w-10 h-10 rounded-xl items-center justify-center mr-3"
                      style={{ backgroundColor: colors.primary[50] }}
                    >
                      <Key size={20} color={colors.primary[600]} />
                    </View>
                    <View className="flex-1">
                      <Text style={{ color: colors.text.primary }} className="font-medium">
                        Change Password
                      </Text>
                      <Text style={{ color: colors.text.tertiary }} className="text-sm">
                        Update your password regularly
                      </Text>
                    </View>
                    <RefreshCw size={18} color={colors.text.tertiary} />
                  </AnimatedPressable>
                </View>
              </Animated.View>

              {/* AI Processing Consent */}
              <Animated.View entering={FadeInUp.delay(450).duration(500)} className="mt-6">
                <View
                  className="rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.border.light,
                  }}
                >
                  <AnimatedPressable
                    onPress={() => setShowAiConsentManager(true)}
                    className="flex-row items-center p-5"
                    accessibilityRole="button"
                    accessibilityLabel="Manage AI processing consent"
                  >
                    <View
                      className="w-10 h-10 rounded-xl items-center justify-center mr-3"
                      style={{ backgroundColor: colors.primary[50] }}
                    >
                      <Sparkles size={20} color={colors.primary[600]} />
                    </View>
                    <View className="flex-1">
                      <Text style={{ color: colors.text.primary }} className="font-medium">
                        AI processing
                      </Text>
                      <Text style={{ color: colors.text.tertiary }} className="text-sm">
                        See what you've consented to, and withdraw it
                      </Text>
                    </View>
                    <ChevronRight size={18} color={colors.text.tertiary} />
                  </AnimatedPressable>
                </View>
              </Animated.View>

              {/* Security Tips */}
              <Animated.View entering={FadeInUp.delay(500).duration(500)} className="mt-6">
                <Text style={{ color: colors.text.secondary }} className="text-sm font-semibold uppercase tracking-wide mb-3 px-1">
                  Security Tips
                </Text>
                <View
                  className="rounded-2xl p-4"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.border.light,
                  }}
                >
                  <View className="gap-3">
                    {[
                      'Use a unique, strong password',
                      'Never share your login credentials',
                      'Enable 2FA for extra protection',
                      'Sign out from shared devices',
                    ].map((tip, index) => (
                      <View key={index} className="flex-row items-center gap-3">
                        <View
                          className="w-6 h-6 rounded-full items-center justify-center"
                          style={{ backgroundColor: colors.primary[50] }}
                        >
                          <Text style={{ color: colors.primary[600] }} className="text-xs font-bold">
                            {index + 1}
                          </Text>
                        </View>
                        <Text style={{ color: colors.text.secondary }} className="text-sm flex-1">
                          {tip}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              </Animated.View>
            </View>
          </KeyboardAwareBody>
        </SafeAreaView>
      </View>

      <AiConsentManagerModal
        visible={showAiConsentManager}
        onClose={() => setShowAiConsentManager(false)}
      />
    </AppLayout>
  );
}
